import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { LanguageValuesHelper } from "@iconics/localisation";
import {
    Card,
    GridContainer,
    GridItem,
    LayoutContainer,
    LayoutContent,
} from "@iconics/components";
import { Guid, PagedResponse, TypeHelper } from "@iconics/core";
import { SerendieSymbol } from "@serendie/symbols";
import {
    IOntologyNodeData,
    INodeData,
    INode2SolutionData,
    IRelationshipData,
    IGraphResponseData,
    IPatchNodeResponse,
} from "./DataModels";
import { RootNodeId } from "../../../../Providers.StaticData/StaticDataProvider";
import { GraphView, IGraphEdge, IGraphNode, IOntologyNode } from "./GraphView";
import AddRelationshipDialog from "./AddRelationshipDialog";
import NodeDetailsSidePanel, { INodeChangeDetails } from "./NodeDetailsSidePanel";

export class NodesGraphView extends IbssPage<
    INodesGraphViewProps,
    INodesGraphViewState
> {
    private languageValuesHelper: LanguageValuesHelper;
    private resolve: (
        value: IGraphEdge | undefined | PromiseLike<IGraphEdge | undefined>,
    ) => void = (v) => {};
    private rootNode: INodeData | undefined;

    constructor(props: INodesGraphViewProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(
            appContext().localisation,
        );

        this.state = {
            nodes: [],
            edges: [],
            ontologyNodes: [],
            availableRelationships: [],
            refreshTreeToken: Guid.new().toString(),
            showAddRelationshipDialog: false,
            storedLayout: "",
            selectedNodeId: undefined,
            refreshToken: '',
        };
    }

    public async componentDidMount()
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_AssetWorX_MenuTitle;
        this.backUrl = `/admin/nodes/tree-view?expanded-node-id=${this.props.params.rootNodeId}`;

        const rootNodeId = Number.parseInt(
            this.props.params.rootNodeId ?? RootNodeId.toString(),
        );

        this.rootNode = 
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.get<INodeData>(
                {
                    tenancy: appContext().tenantId,
                    nodeId: rootNodeId,
                },
            );

        this.setState({
            storedLayout: this.rootNode?.Layout ?? "",
        });

        await this.populateGraph(rootNodeId);
    }

    private async populateGraph(rootNodeId: number): Promise<void>
    {
        const graphData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.descendants.get<IGraphResponseData>(
            {
                tenancy: appContext().tenantId,
                nodeId: rootNodeId,
            }
        );

        const ontologyNodes = this.buildOntologyNodeCache(graphData.OntologyNodes);
        const nodes = await this.populateNodes(graphData);

        const edges = await this.populateEdges(nodes, graphData);

        // Stamp a fresh refresh token in the same setState as nodes/edges so the child
        // sees the new token together with the freshly-populated data — every token
        // change triggers syncAndLayout in GraphView.componentDidUpdate. To force a
        // re-sync later (e.g. after a save), just stamp another Guid here.
        await this.setStateAsync({
            nodes: nodes,
            edges: edges,
            ontologyNodes: ontologyNodes,
            refreshToken: Guid.new().toString(),
        });
    }

    private buildOntologyNodeCache(rawOntologyNodes: IOntologyNodeData[]): IOntologyNode[]
    {
        return rawOntologyNodes.map(n =>
        {
            let name = this.languageValuesHelper.getValue(
                this.languageValuesHelper.fromString(n.DisplayName),
            );
            if (!name)
            {
                name = n.Name;
            }
            let styling: { [key: string]: string };
            try
            {
                styling = JSON.parse(n.Styling);
            }
            catch
            {
                styling = { color: "", "background-color": "" };
            }
            return { id: n.ID, name, styling } satisfies IOntologyNode;
        });
    }

    private async populateEdges(nodes: IGraphNode[], graphData: IGraphResponseData): Promise<IGraphEdge[]>
    {
        const parentOfs = nodes
            .filter((n) => n.parentId)
            .map((n) => ({
                id: n.id.toString() + "_" + n.parentId.toString(),
                relationshipId: '',
                displayName: { "en-GB": "Hierarchy" },
                name: "Hierarchy",
                source: nodes.find((m) => m.id === n.parentId),
                destination: n,
                hierarchical: true,
            }));

        const relDetailsResult = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.relationships.get<
            PagedResponse<IRelationshipData[]>
        >({
            tenancy: appContext().tenantId,
        });

        const relDetails = relDetailsResult.value;

        await this.setStateAsync({
            availableRelationships: relDetails,
        });

        const relMap = new Map(relDetails.map((r) => [r.Id, r]));

        const otherEdges: IGraphEdge[] = graphData.RelationshipLinks.map(
            (link) => {
                const detail = relMap.get(link.Relationship_ID);

                return {
                    id: `${link.PrimaryNode_ID}_${link.SecondaryNode_ID}_${link.Relationship_ID}`,
                    relationshipId: link.Relationship_ID,
                    displayName: detail
                        ? this.languageValuesHelper.fromString(detail.DisplayName)
                        : this.languageValuesHelper.default(),
                    name: detail ? detail.Name : "",
                    source: nodes.find((n) => n.id === link.PrimaryNode_ID),
                    destination: nodes.find((n) => n.id === link.SecondaryNode_ID),
                    hierarchical: false,
                    styling: detail?.Styling
                };
            }
        );

        const edges = [...parentOfs, ...otherEdges];
        return edges;
    }

    private async populateNodes(graphData: IGraphResponseData): Promise<IGraphNode[]>
    {
        let nodes = graphData.Nodes.map(
            (nodeData) => ({
                id: nodeData.Id,
                parentId: nodeData.Parent_ID ?? undefined,
                name: nodeData.Name,
                displayName: this.languageValuesHelper.fromString(
                    nodeData.DisplayName
                ),
                isEnabled: TypeHelper.toBoolean(nodeData.IsEnabled),
                ontologyNodeIds: graphData.OntologyNodeLinks
                    .filter((link) => link.Node_ID === nodeData.Id)
                    .map((link) => link.OntologyNode_ID),
                solutionNames: [],
            }) as IGraphNode
        );

        nodes = await this.handleGetSolutions(nodes);

        return nodes;
    }

    private async handleGetSolutions(nodes: IGraphNode[]): Promise<IGraphNode[]>
    {
        const promises = nodes.map(async (node) =>
        {
            const response =
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.solutions.get<
                    PagedResponse<INode2SolutionData[]>
                >({
                    tenancy: appContext().tenantId,
                    nodeId: node.id,
                });

            node.solutionNames = response.value.map((x) => x.Name);
            return node;
        });
        const results = await Promise.all(promises);
        return results.flat();
    }

    private handleAddRelationship(
        source: IGraphNode,
        target: IGraphNode,
    ): Promise<IGraphEdge | undefined> {
        this.setState({
            newRelationshipSource: source,
            newRelationshipTarget: target,
            showAddRelationshipDialog: true,
        });

        const { promise, resolve } = Promise.withResolvers<
            IGraphEdge | undefined
        >();

        this.resolve = resolve;

        return promise;
    }

    private onRelationshipAdded(result: boolean, newRelationshipId = ""): void
    {
        if (!result)
        {
            this.resolve(undefined);
            this.setState({ showAddRelationshipDialog: false });
            return;
        }

        const detail = this.state.availableRelationships.find(
            (r) => r.Id === newRelationshipId,
        );

        const newEdge = {
            id: `${this.state.newRelationshipSource?.id}_${this.state.newRelationshipTarget?.id}_${newRelationshipId}`,
            relationshipId: newRelationshipId,
            displayName: detail
                ? this.languageValuesHelper.fromString(detail.DisplayName)
                : this.languageValuesHelper.default(),
            name: detail ? detail.Name : "",
            source: this.state.newRelationshipSource,
            destination: this.state.newRelationshipTarget,
            hierarchical: false,
            styling: detail?.Styling,
        };

        this.setState({
            newRelationshipSource: undefined,
            newRelationshipTarget: undefined,
            showAddRelationshipDialog: false,
            edges: [...this.state.edges, newEdge],
        });

        this.resolve(newEdge);
    }

    private async saveLayout(layout: string): Promise<void>
    {
        if (!this.rootNode) return;
        const response =
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.patch<IPatchNodeResponse>(
                {
                    nodeId: this.rootNode.Id,
                    tenancy: appContext().tenantId,
                    body: {
                        Layout: layout,
                        "@odata.etag": this.rootNode["@odata.etag"],
                    },
                },
            );

        this.rootNode = response.CreatedObject;
        this.setState({ storedLayout: layout });
    }

    private async handleRemoveRelationship(
        i: string,
        s: IGraphNode,
        t: IGraphNode,
    ): Promise<boolean> {
        try
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.relationships.delete(
                {
                    nodeId: s.id,
                    tenancy: appContext().tenantId,
                    body: {
                        SecondaryNodeid: t.id,
                        RelationshipId: i,
                    },
                },
            );
            return true;
        } catch {
            return false;
        }
    }

    private handleSelectedNodeChange(nodeId: number | undefined): void
    {
        this.setState({ selectedNodeId: nodeId });
    }

    private async handleNodeChanged(details: INodeChangeDetails): Promise<void>
    {
        // Any IDs the side panel reports that aren't already in our cache need
        // their full data (name + styling) fetched before the graph re-renders,
        // otherwise the node card would render the chip with no resolved entry.
        const cached = new Set(this.state.ontologyNodes.map(o => o.id));
        const missing = details.ontologyNodeIds.filter(id => !cached.has(id));
        let ontologyNodes = this.state.ontologyNodes;
        if (missing.length > 0)
        {
            ontologyNodes = await this.fetchAndMergeOntologyNodes(details.nodeId, missing);
        }

        this.setState({
            ontologyNodes,
            nodes: this.state.nodes.map(n =>
                n.id !== details.nodeId
                    ? n
                    : {
                        ...n,
                        name: details.name,
                        displayName: details.displayName,
                        isEnabled: details.isEnabled,
                        ontologyNodeIds: details.ontologyNodeIds,
                    },
            ),
            // Stamp a fresh token so GraphView re-syncs (reconcileNodesWithCurrent
            // preserves positions, so visually only this node's data updates).
            refreshToken: Guid.new().toString(),
        });
    }

    private async fetchAndMergeOntologyNodes(nodeId: number, missingIds: string[]): Promise<IOntologyNode[]>
    {
        // The descendants endpoint is the only one that returns OntologyNodes with
        // their styling. We call it on the changed node and pull entries for any
        // IDs we don't already have cached; anything else returned just gets ignored.
        const graphData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.descendants.get<IGraphResponseData>({
            tenancy: appContext().tenantId,
            nodeId: nodeId,
        });
        const missingSet = new Set(missingIds);
        const newEntries = this.buildOntologyNodeCache(
            graphData.OntologyNodes.filter(o => missingSet.has(o.ID)),
        );
        return [...this.state.ontologyNodes, ...newEntries];
    }

    private handleNavigateToDataPoint(nodeId: number, dataPointId: string | undefined): void
    {
        if (dataPointId)
        {
            window.open(`/admin/nodes/${nodeId}/data-points/${dataPointId}/edit`, '_blank');
        }
        else
        {
            window.open(`/admin/nodes/${nodeId}/data-points/new`, '_blank');
        }
    }

    private async handleSidePanelDelete(nodeId: number): Promise<void>
    {
        try
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.delete({
                nodeId: nodeId,
                tenancy: appContext().tenantId,
            });
        }
        catch
        {
            return;
        }

        this.setState({
            nodes: this.state.nodes.filter(n => n.id !== nodeId),
            edges: this.state.edges.filter(e => e.source?.id !== nodeId && e.destination?.id !== nodeId),
            // Stamp a fresh token so GraphView re-syncs (reconcileNodesWithCurrent
            // preserves positions, so visually only this node's removed).
            refreshToken: Guid.new().toString(),
        });

        this.handleSelectedNodeChange(undefined);
    }


    public render(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            icon={<SerendieSymbol name="drag-move" />}
                            title={labels.Platform_AssetWorX_NodeGraphTitle}
                            subTitle={labels.Platform_AssetWorX_NodeGraphSubTitle}
                        >
                            <GridContainer>
                                <GridItem expanded={8}>
                                    <GraphView
                                        rootNodeId={
                                            this.props.params.rootNodeId ?? RootNodeId.toString()
                                        }
                                        nodes={this.state.nodes}
                                        edges={this.state.edges}
                                        ontologyNodes={this.state.ontologyNodes}
                                        refreshToken={this.state.refreshToken}
                                        onAddRelationship={(s, t) =>
                                            this.handleAddRelationship(s, t)
                                        }
                                        onRemoveRelationship={(i, s, t) =>
                                            this.handleRemoveRelationship(i, s, t)
                                        }
                                        storedLayout={this.state.storedLayout}
                                        onSaveLayout={(layout) => this.saveLayout(layout)}
                                        onSelectedNodeChange={(i) => this.handleSelectedNodeChange(i)}
                                    />
                                </GridItem>
                                <GridItem expanded={4} className="graph-view__side-panel-column">
                                    <div className="graph-view__side-panel-scroll">
                                        <NodeDetailsSidePanel
                                            nodeId={this.state.selectedNodeId}
                                            graphRefreshToken={this.state.refreshToken}
                                            onNodeChanged={(details) => this.handleNodeChanged(details)}
                                            onDelete={(nodeId) => this.handleSidePanelDelete(nodeId)}
                                            onNavigateToDataPoint={(nodeId, dataPointId) => this.handleNavigateToDataPoint(nodeId, dataPointId)}
                                        />
                                    </div>
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
                <AddRelationshipDialog
                    open={this.state.showAddRelationshipDialog}
                    onClose={() => this.onRelationshipAdded(false)}
                    onSave={(id) => this.onRelationshipAdded(true, id)}
                    source={this.state.newRelationshipSource}
                    target={this.state.newRelationshipTarget}
                    relationships={this.state.availableRelationships}
                />
            </>
        );
    }
}

export default withRouting(NodesGraphView);

export interface INodesGraphViewProps extends IRouterProps<IQueryParams> { }

export interface INodesGraphViewState
{
    nodes: IGraphNode[];
    edges: IGraphEdge[];
    // Cache of every ontology node referenced by any IGraphNode in `nodes`.
    // IGraphNode.ontologyNodeIds resolves into this list on the GraphView side.
    ontologyNodes: IOntologyNode[];
    availableRelationships: IRelationshipData[];
    refreshTreeToken: string;
    newRelationshipSource?: IGraphNode;
    newRelationshipTarget?: IGraphNode;
    showAddRelationshipDialog: boolean;
    storedLayout: string;
    selectedNodeId: number | undefined;
    refreshToken: string;
}

export interface IQueryParams
{
    rootNodeId: string | undefined;
}
